import { Circle, keyframes, SquareProps } from '@chakra-ui/react';
import { GoalType } from '@common/graphql/generated';
import styleguide from '@common/styleguide';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { arc } from '@visx/shape';
import chroma from 'chroma-js';
import { motion } from 'framer-motion';
import { Text } from '../common/Typography';
import { configByGoalType } from './config';
import GoalIconCircle from './GoalIconCircle';

export interface GoalProgressCircleProps extends SquareProps {
    type: GoalType;
    progress: number;
    hideProgress?: boolean;
}

const GoalProgressCircle = ({
    type,
    progress,
    hideProgress,
    ...props
}: GoalProgressCircleProps) => {
    const { color } = configByGoalType[type];
    const secondaryGradientColor = '#7ACB78';

    if (hideProgress) {
        return (
            <Circle
                position="relative"
                size={20}
                bg="white"
                overflow="hidden"
                {...props}
            >
                <Circle
                    position="absolute"
                    top={0}
                    left={0}
                    size="100%"
                    bgGradient={`radial(${secondaryGradientColor} 9%, ${color} 33%)`}
                    animation={`${rotateNeg360} 6s linear infinite, ${reversePulseOpacity} 6s linear infinite`}
                />
                <Circle
                    position="absolute"
                    top={0}
                    left={0}
                    size="100%"
                    bgGradient={`linear(to-br, ${secondaryGradientColor}, ${color})`}
                    animation={`${rotate360} 6s linear infinite, ${pulseOpacity} 4s linear infinite`}
                />
                <GoalIconCircle
                    position="relative"
                    size={10}
                    iconSize={5}
                    type={type}
                    inverted
                    shadow="md"
                    zIndex={1}
                />
            </Circle>
        );
    }

    return (
        <Circle position="relative" overflow="hidden" size={20} {...props}>
            <Circle position="absolute" size="100%" bg="gray.100">
                <ParentSize>
                    {({ width }) => {
                        const size = width;
                        const arcGenerator = arc({
                            outerRadius: size / 2,
                            innerRadius: 0,
                        });

                        const leftHalfPath =
                            arcGenerator({
                                startAngle: 2 * Math.PI,
                                endAngle: leftAngleScale(progress),
                            }) ?? undefined;

                        const rightHalfPath =
                            arcGenerator({
                                startAngle: Math.PI,
                                endAngle: rightAngleScale(progress),
                            }) ?? undefined;

                        return (
                            <motion.svg
                                width={size}
                                height={size}
                                viewBox={`0 0 ${size} ${size}`}
                                fill="none"
                                color="blue"
                            >
                                <Group top={size / 2} left={size / 2}>
                                    <motion.path
                                        fill={color}
                                        d={leftHalfPath}
                                    />
                                    <motion.path
                                        fill={color}
                                        d={rightHalfPath}
                                    />
                                </Group>
                            </motion.svg>
                        );
                    }}
                </ParentSize>
            </Circle>

            {progress >= 100 && (
                <Circle
                    size={16}
                    position="absolute"
                    background={chroma(styleguide.colors.yellow).alpha(1).hex()}
                    boxShadow={`0 0 0 0 ${chroma(styleguide.colors.yellow)
                        .alpha(1)
                        .hex()}`}
                    animation={`${pulseColor(
                        styleguide.colors.yellow
                    )} 2s infinite`}
                />
            )}
            <Circle size={13} zIndex={1} shadow="md" bg="white">
                <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={chroma(color).darken(0.5).hex()}
                >
                    {progress < 1 && progress > 0
                        ? Math.ceil(progress * 10) / 10
                        : Math.floor(progress)}
                    %
                </Text>
            </Circle>
        </Circle>
    );
};

const leftAngleScale = scaleLinear({
    domain: [0, 50],
    range: [2 * Math.PI, Math.PI],
    clamp: true,
});
const rightAngleScale = scaleLinear({
    domain: [50, 100],
    range: [Math.PI, 0],
    clamp: true,
});

const pulseOpacity = keyframes`
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 0.5;
  }
`;
const reversePulseOpacity = keyframes`
  0% {
    opacity: 0.8;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 0.8;
  }
`;
const rotate360 = keyframes`
  from {
    transform-origin: center center 50%;
    transform: rotate(0deg);
  }
  to {
    transform-origin: center center 50%;
    transform: rotate(360deg);
  }
`;
const rotateNeg360 = keyframes`
  from {
    transform-origin: center top 50%;
    transform: rotate(0deg);
  }
  to {
    transform-origin: center top 50%;
    transform: rotate(-360deg);
  }
`;
const pulseColor = (color: string) => keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 ${chroma(color).alpha(0.7).hex()};
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px ${chroma(color).alpha(0).hex()};
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 ${chroma(color).alpha(0).hex()};
  }
`;

export default GoalProgressCircle;
